import { functionDoc } from '../functionDoc';
import { ROLES, SHOTS } from '../docHelpers';

/**
 * Guía canónica de la grilla de productos/ítems (componentes compartidos:
 * QuickProductLineBar, LineItemField, captura rápida, Excel, conversiones).
 */
export const grillaProductosItemsDoc = functionDoc({
  id: 'general-grilla-productos-items',
  name: 'Grilla de productos e ítems',
  type: 'screen',
  route: '/manual',
  locationPath:
    'Varios formularios → bloque «Ítems», «Productos» o líneas del documento (ver tabla en observaciones)',
  generalDescription:
    'Patrón de interfaz repetido en Contifine Logistic para cargar líneas de productos (y en Ventas también servicios) en documentos comerciales y de inventario. Cada línea es una fila con selector de producto, cantidad, unidad cuando aplica, precio, IGV por línea si está activo, proyecto y subtotal calculado. Encima puede aparecer la barra «Captura rápida» (borde verde) y los valores «Por defecto en productos» (proyecto, IGV, tipo de venta en Ventas). Debajo se listan las filas ya agregadas; el botón ✕ quita una fila sin guardar el documento. Muchos formularios permiten descargar plantilla Excel e importar ítems con vista previa. Los totales del documento se recalculan al editar líneas; el kardex solo se mueve al confirmar guías o al registrar ventas/salidas, no mientras edita la grilla.',
  prerequisites: [
    'Productos activos en Gestión → Productos (y servicios en Ventas si corresponde).',
    'Unidades y, si aplica, conversiones y proyectos configurados.',
    'Flag «línea rápida de producto» (quick product line) y demás opciones en Administrador → Configuraciones generales / módulos.',
    'Permiso de escritura en el módulo donde opera (Compras, Ventas, Almacenes).',
  ],
  interfaceElements: [
    {
      name: 'Dónde aparece esta grilla',
      controlType: 'Referencia',
      required: false,
      editable: false,
      automatic: true,
      description:
        'Ventas: cotización, orden de servicio de venta (solo servicios), registrar venta. Compras: requerimiento, orden de compra. Almacenes: guías de ingreso, salida, traslado, recepción, ajuste e inventario inicial (según tipo).',
      behavior:
        'La etiqueta del bloque puede ser «Ítems» o «Productos»; las columnas visibles dependen del módulo y de los flags activos.',
    },
    {
      name: 'Acciones de la barra superior',
      controlType: 'Botones',
      required: false,
      editable: true,
      automatic: false,
      description:
        '+ Producto / + Agregar línea / + Servicio (Ventas), Descargar plantilla, Importar Excel, enlace a requerimientos u OC en Compras.',
      behavior:
        'En solo lectura no se muestran. OSV de venta no importa Excel ni productos. Requerimiento no maneja precios ni IGV en la grilla.',
    },
    {
      name: 'Captura rápida',
      controlType: 'Barra con borde verde',
      required: false,
      editable: true,
      automatic: false,
      description: 'Captura producto, cantidad, unidad y precio y pulsa Agregar.',
      behavior:
        'Sustituye al botón + Producto cuando está habilitada en configuración. La tecla Enter en esta zona no envía el formulario principal.',
    },
    {
      name: 'Por defecto en productos',
      controlType: 'Barra de selects',
      required: false,
      editable: true,
      automatic: false,
      description: 'Proyecto, IGV y (solo Ventas) tipo de venta para líneas nuevas.',
      behavior: 'Visible según flags de proyecto, IGV en productos y tipos de venta del POS.',
    },
    {
      name: 'Precios incluyen IGV (conmutador)',
      controlType: 'Toggle',
      required: false,
      editable: true,
      automatic: false,
      description: 'En Compras (OC) y guías de almacén con valorización.',
      behavior:
        'Cambia cómo se interpreta el precio tipeado respecto al IGV. En Ventas los precios de línea se ingresan siempre sin IGV en pantalla.',
    },
    {
      name: 'Subtotal con: medida convertida / medida ingresada',
      controlType: 'Conmutador',
      required: false,
      editable: true,
      automatic: false,
      description: 'Con conversiones de unidad activas y tasa personalizada en la línea.',
      behavior: 'Define qué cantidad multiplica el precio unitario en el subtotal.',
    },
    {
      name: 'Producto',
      controlType: 'Buscador',
      required: true,
      editable: true,
      automatic: false,
      description: 'Catálogo de productos; en Ventas puede incluir ofertas del POS.',
      behavior: 'Al elegir producto suele traer unidad y precio sugerido (lista o último costo según pantalla).',
      example: 'SKU 04012 — Aceite 1 L',
    },
    {
      name: 'Unidad',
      controlType: 'Select',
      required: false,
      editable: true,
      automatic: false,
      description: 'Unidad de medida de la línea (Compras, guías, requerimiento).',
      behavior: 'En Ventas la unidad suele venir del producto; la ayuda bajo cantidad muestra equivalencias.',
    },
    {
      name: 'Servicio y descripción',
      controlType: 'Selector + texto',
      required: false,
      editable: true,
      automatic: false,
      description: 'Solo formularios de Ventas con líneas de servicio.',
      behavior: 'Descripción obligatoria en servicios para PDF y comprobante.',
    },
    {
      name: 'Proyecto',
      controlType: 'Select',
      required: false,
      editable: true,
      automatic: false,
      description: 'Centro de costo por línea si el módulo de proyecto está activo.',
      behavior: 'Hereda valor por defecto de la barra superior o del POS.',
    },
    {
      name: 'Cantidad',
      controlType: 'Número',
      required: true,
      editable: true,
      automatic: false,
      description: 'Cantidad en la unidad indicada.',
      behavior: 'Debe ser mayor que cero para guardar.',
    },
    {
      name: 'Precio unitario',
      controlType: 'Moneda',
      required: false,
      editable: true,
      automatic: false,
      description: 'Precio por unidad; en requerimiento puede omitirse.',
      behavior:
        'Texto de ayuda (PriceIgvHint) puede mostrar equivalente con o sin IGV. Moneda de cabecera en ventas/guías dual currency.',
    },
    {
      name: 'IGV por línea',
      controlType: 'Sí / No',
      required: false,
      editable: true,
      automatic: false,
      description: 'Gravado o no gravado a la tasa de cabecera.',
      behavior: 'Visible si IGV por producto está habilitado en configuración.',
    },
    {
      name: 'Subtotal de línea',
      controlType: 'Solo lectura',
      required: false,
      editable: false,
      automatic: true,
      description: 'Importe calculado de la fila.',
      behavior: 'Se actualiza al cambiar cantidad, precio, unidad o modo de conversión.',
    },
    {
      name: 'Quitar línea (✕)',
      controlType: 'Botón',
      required: false,
      editable: true,
      automatic: false,
      description: 'Elimina la fila del borrador.',
      behavior: 'No revierte movimientos ya confirmados en kardex.',
    },
    {
      name: 'Importar Excel',
      controlType: 'Archivo + modal',
      required: false,
      editable: true,
      automatic: false,
      description: 'Plantilla con SKU/código, cantidad y precio (según módulo).',
      behavior: 'Muestra previsualización con errores por fila antes de confirmar.',
    },
  ],
  procedure: [
    'Abra el documento (OC, guía, cotización, etc.) y complete la cabecera si el formulario lo exige.',
    'Revise si ve «Captura rápida» o debe usar + Producto / + Agregar línea.',
    'Opcional: defina proyecto e IGV por defecto en la barra superior.',
    'Agregue líneas una a una o importe desde Excel; confirme la previsualización.',
    'En Ventas con servicios, complete la descripción de cada línea de servicio.',
    'Ajuste cantidad, unidad o precio; use el conmutador de subtotal si hay conversión de unidades.',
    'Revise subtotales por fila y totales del documento antes de guardar o confirmar.',
    'Guarde; en guías y ventas verifique kardex o stock según el tipo de movimiento.',
  ],
  practicalExample: {
    context: 'Orden de compra con tres insumos y unidad distinta al stock.',
    data: {
      Módulo: 'Compras',
      Pantalla: 'Orden de compra — nueva',
      Acción: 'Captura rápida + una línea con unidad KG',
    },
    steps: [
      'Seleccione proveedor y fecha.',
      'En captura rápida agregue dos productos con cantidades de la cotización del proveedor.',
      'Tercera línea: cambie unidad en el selector y verifique la equivalencia bajo Cantidad.',
      'Active o desactive «Precios incluyen IGV» según la factura del proveedor.',
      'Guarde la OC y use el avance para recepcionar en guía de ingreso.',
    ],
  },
  validations: [
    'Al menos una línea con producto y cantidad > 0 (salvo borradores que el formulario permita vacíos).',
    'Producto obligatorio en cada fila de producto; servicio y descripción en líneas de servicio (Ventas).',
    'Importación: SKU debe existir; filas con error no deben confirmarse sin corregir.',
    'Referencias (OC en guía, cotización en venta): cantidades no deben superar el pendiente del origen.',
    'Salidas de almacén y ventas: stock suficiente al confirmar, no necesariamente al tipear.',
  ],
  expectedResult: [
    'Documento guardado con líneas coherentes y totales que cuadran.',
    'En inventario: movimiento de kardex tras confirmar guía o venta según reglas del tipo.',
    'En Compras: avance de OC/requerimiento actualizado al recepcionar o facturar.',
  ],
  commonErrors: [
    {
      error: 'Grilla vacía o no guarda',
      cause: 'Sin líneas válidas o cantidades en cero.',
      solution: 'Complete al menos un producto con cantidad positiva.',
    },
    {
      error: 'Excel no importa filas',
      cause: 'Plantilla incorrecta, SKU desconocido o no confirmó el modal.',
      solution: 'Descargue plantilla del mismo formulario, corrija códigos y confirme vista previa.',
    },
    {
      error: 'Precio o subtotal no cuadra con el proveedor',
      cause: 'Toggle «precios incluyen IGV» distinto al criterio de la factura.',
      solution: 'Alinee el conmutador y la tasa de IGV de cabecera; revise PriceIgvHint bajo el precio.',
    },
    {
      error: 'No aparece captura rápida',
      cause: 'Flag desactivado en configuración general.',
      solution: 'Use + Producto o solicite al administrador habilitar línea rápida.',
    },
    {
      error: 'Stock insuficiente',
      cause: 'Salida, traslado o venta supera saldo.',
      solution: 'Reduzca cantidad, ingrese mercadería o revise almacén/POS.',
    },
  ],
  relatedFunctions: [
    'general-grilla-detalle-controles-comunes',
    'general-grilla-detalle-requerimiento-compras',
    'general-grilla-detalle-oc-compras',
    'general-grilla-detalle-os-compras',
    'general-grilla-detalle-ventas-producto',
    'general-grilla-detalle-ventas-servicio',
    'general-grilla-detalle-guia-ingreso',
    'general-grilla-detalle-guia-salida-traslado',
    'ventas-grilla-productos-items',
    'compras-grilla-productos-items',
    'almacenes-grilla-productos-items',
  ],
  observations: [
    'El detalle campo por campo (cada input y select) está en las fichas «Grilla — fila …» de este mismo módulo Inicio y acceso.',
    'No sustituye las fichas de Cotización, OC o Guía: complementa la tabla de columnas.',
    ROLES.limitado,
  ],
  screenshots: [
    SHOTS.formularioCompleto,
    'Captura rápida con dos filas de producto debajo.',
    'Modal de previsualización de importación Excel.',
  ],
  roles: 'Cualquier usuario que registre documentos con líneas de producto.',
});
